// file: src/layouts/DashboardLayout.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "./BrandLogo";

const BLUE = "#1271EB";
function clsx(...p) {
  return p.filter(Boolean).join(" ");
}

/* Icons */
const IcMenu = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);
const IcClose = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
);
const IcHome = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 10v10h14V10" />
  </svg>
);
const IcCheck = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M4 12l5 5 11-11" />
  </svg>
);
const IcBan = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M5 19L19 5" />
  </svg>
);

function SidebarLink({ to, icon: Icon, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          "group relative flex items-center gap-3 rounded-xl px-3 py-2 text-[15px] transition",
          isActive
            ? "bg-[rgba(18,113,235,0.06)] text-black shadow-sm"
            : "text-black/70 hover:text-black hover:bg-black/[0.035]"
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={clsx(
              "absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full",
              isActive ? "bg-[color:var(--blue)]" : "bg-transparent"
            )}
            style={{ ["--blue"]: BLUE }}
          />
          <Icon
            className={clsx(
              "h-5 w-5",
              isActive
                ? "text-[color:var(--blue)]"
                : "text-black/60 group-hover:text-black"
            )}
            style={{ ["--blue"]: BLUE }}
          />
          <span className="font-medium tracking-tight">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function DashboardLayout({ children, onLogout }) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = prev;
      };
    }
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    try {
      const keys = [
        "access_token",
        "token_type",
        "admin_user",
        "auth_access_token",
        "auth_token_type",
        "auth_user",
        "auth_remember",
      ];
      keys.forEach((k) => {
        try {
          localStorage.removeItem(k);
          sessionStorage.removeItem(k);
        } catch {}
      });

      try {
        localStorage.clear();
      } catch {}
      try {
        sessionStorage.clear();
      } catch {}

      try {
        window.dispatchEvent(new CustomEvent("auth:logout"));
      } catch {}
    } catch {
      /* no-op */
    }

    onLogout?.();

    navigate("/admin/login", { replace: true });
    try {
      window.location.replace("/admin/login");
    } catch {}
  };

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden">
      {/* Header: full-bleed on desktop */}
      <header className="sticky top-0 z-40 border-b border-black/[0.06] bg-white/80 backdrop-blur">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8 xl:px-12">
          <div className="flex items-center gap-3">
            <button
              className="mr-1 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-black/10 bg-white lg:hidden"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              {/* IcMenu */}
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
            <BrandLogo size={36} />
            <div className="ml-2">
              <div className="text-base font-semibold leading-6 tracking-tight">
                Towing Admin Portal
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-2xl bg-[color:var(--blue)] px-4 py-2.5 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition"
            style={{ ["--blue"]: BLUE, boxShadow: `0 10px 24px ${BLUE}33` }}
          >
            Log out
          </button>
        </div>
      </header>

      {/* Main: sidebar flush left, content centered */}
      <div className="grid min-h-[calc(100vh-4rem)] grid-cols-1 lg:grid-cols-[280px,1fr] w-full max-w-full">
        <aside className="hidden lg:block">
          <div className="sticky top-20 h-[calc(100vh-5rem)] border-r border-black/[0.06] bg-white px-4 py-5">
            <div
              className="mb-4 h-12 rounded-xl"
              style={{
                background:
                  "linear-gradient(135deg, rgba(18,113,235,0.08), rgba(18,113,235,0.02))",
              }}
            />
            <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-black/40">
              Main
            </div>
            <nav className="flex flex-col gap-1">
              <SidebarLink
                to="/admin/dashboard"
                label="Dashboard"
                icon={(p) => (
                  <svg
                    {...p}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M3 10.5 12 3l9 7.5" />
                    <path d="M5 10v10h14V10" />
                  </svg>
                )}
              />
              <SidebarLink
                to="/admin/valid-reservations"
                label="Valid Reservations"
                icon={(p) => (
                  <svg
                    {...p}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M4 12l5 5 11-11" />
                  </svg>
                )}
              />
              <SidebarLink
                to="/admin/do-not-tow"
                label="Do Not Tow List"
                icon={(p) => (
                  <svg
                    {...p}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M5 19L19 5" />
                  </svg>
                )}
              />
              <SidebarLink
                to="/admin/access-logs"
                label="Activity"
                icon={(p) => (
                  <svg
                    {...p}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {/* Shield outline */}
                    <path d="M12 3c3.5 0 7 1.6 7 4.8v3.2c0 5.2-3.4 8.5-7 10-3.6-1.5-7-4.8-7-10V7.8C5 4.6 8.5 3 12 3z" />
                    {/* Keyhole */}
                    <circle cx="12" cy="11" r="1.6" />
                    <path d="M12 12.6v2.2" />
                  </svg>
                )}
                onClick={() => setOpen(false)}
              />
            </nav>
            <div className="mt-6 rounded-2xl border border-black/[0.06] p-3">
              <div className="text-xs text-black/50">Session</div>
              <div className="mt-1 flex items-center justify-between">
                <div className="text-sm font-medium">Admin</div>
                <span
                  className="rounded-full px-2 py-0.5 text-xs text-white"
                  style={{ background: BLUE }}
                >
                  Active
                </span>
              </div>
            </div>
          </div>
        </aside>

        <main className="w-full max-w-full min-w-0 overflow-x-hidden">
          <div className="mx-auto w-full max-w-7xl min-w-0 px-4 py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Drawer (<lg): closes on overlay, Esc, and route change */}
      <div
        ref={sheetRef}
        className={clsx(
          "fixed inset-0 z-50 lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none"
        )}
        onMouseDown={(e) => e.target === sheetRef.current && setOpen(false)}
      >
        <div
          className={clsx(
            "absolute inset-0 bg-black/40 transition-opacity",
            open ? "opacity-100" : "opacity-0"
          )}
        />
        <div
          className={clsx(
            "absolute inset-y-0 left-0 w-[86%] max-w-[320px] bg-white shadow-2xl transition-transform will-change-transform",
            open ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-3">
            <div className="flex items-center gap-2">
              <BrandLogo size={28} />
              <div className="text-sm font-semibold tracking-tight">
                Towing Admin Portal
              </div>
            </div>
            <button
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-black/[0.08] hover:bg-black/[0.03] active:scale-95 transition"
            >
              {/* IcClose */}
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>
          <nav className="p-3">
            <SidebarLink
              to="/admin/dashboard"
              label="Dashboard"
              icon={(p) => (
                <svg
                  {...p}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 10.5 12 3l9 7.5" />
                  <path d="M5 10v10h14V10" />
                </svg>
              )}
              onClick={() => setOpen(false)}
            />
            <SidebarLink
              to="/admin/valid-reservations"
              label="Valid Reservations"
              icon={(p) => (
                <svg
                  {...p}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M4 12l5 5 11-11" />
                </svg>
              )}
              onClick={() => setOpen(false)}
            />
            <SidebarLink
              to="/admin/do-not-tow"
              label="Do Not Tow List"
              icon={(p) => (
                <svg
                  {...p}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M5 19L19 5" />
                </svg>
              )}
              onClick={() => setOpen(false)}
            />

            <SidebarLink
              to="/admin/access-logs"
              label="Activity"
              icon={(p) => (
                <svg
                  {...p}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {/* Shield outline */}
                  <path d="M12 3c3.5 0 7 1.6 7 4.8v3.2c0 5.2-3.4 8.5-7 10-3.6-1.5-7-4.8-7-10V7.8C5 4.6 8.5 3 12 3z" />
                  {/* Keyhole */}
                  <circle cx="12" cy="11" r="1.6" />
                  <path d="M12 12.6v2.2" />
                </svg>
              )}
              onClick={() => setOpen(false)}
            />
          </nav>
          <div className="m-3 rounded-2xl border border-black/[0.06] p-3">
            <div className="text-xs text-black/50">Session</div>
            <div className="mt-1 flex items-center justify-between">
              <div className="text-sm font-medium">Admin</div>
              <button
                onClick={() => {
                  setOpen(false);
                  handleLogout();
                }}
                className="rounded-lg px-2.5 py-1 text-xs text-white transition active:scale-[0.98]"
                style={{ background: BLUE, boxShadow: `0 6px 16px ${BLUE}26` }}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
